import React, { useState, useEffect } from 'react';
import { Wallet, Plus, Copy, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { apiDelete, apiFetch, apiGet } from '../../lib/apiClient';
import { API_ROUTES } from '../../lib/apiRoutes';
import { AdminConfirmDialog } from '../../components/AdminUi';

const NETWORK_OPTIONS = [
  { value: 'USDT_TRC20', label: 'USDT (TRC20)' },
  { value: 'USDT_ERC20', label: 'USDT (ERC20)' },
  { value: 'BTC', label: 'Bitcoin (BTC)' },
  { value: 'ETH', label: 'Ethereum (ETH)' },
  { value: 'BNB_BSC', label: 'BNB (BSC)' },
  { value: 'LTC', label: 'Litecoin (LTC)' },
];

export default function AdminWallets() {
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newWallet, setNewWallet] = useState({ network: 'USDT_TRC20', address: '', label: '' });
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => { fetchWallets(); }, []);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const data = await apiGet(API_ROUTES.admin.wallets);
      setWallets(data.wallets || []);
    } catch (e) {
      toast.error('Failed to load wallets');
    } finally {
      setLoading(false);
    }
  };

  const createWallet = async () => {
    if (!newWallet.address.trim()) {
      toast.error('Wallet address is required');
      return;
    }
    try {
      const res = await apiFetch(API_ROUTES.admin.wallets, {
        method: 'POST',
        body: newWallet,
      });
      if (!res.ok) throw new Error('Failed');
      toast.success('Wallet added');
      setCreating(false);
      setNewWallet({ network: 'USDT_TRC20', address: '', label: '' });
      fetchWallets();
    } catch (e) {
      toast.error('Failed to add wallet');
    }
  };

  const toggleWallet = async (id, currentActive) => {
    try {
      await apiFetch(`${API_ROUTES.admin.wallets}/${id}`, {
        method: 'PATCH',
        body: { isActive: !currentActive },
      });
      toast.success(currentActive ? 'Wallet disabled' : 'Wallet enabled');
      fetchWallets();
    } catch (e) {
      toast.error('Failed to update wallet');
    }
  };

  const deleteWallet = async () => {
    if (!pendingDelete) return;
    try {
      await apiDelete(`${API_ROUTES.admin.wallets}/${pendingDelete.id}`);
      toast.success('Wallet deleted');
      setPendingDelete(null);
      fetchWallets();
    } catch (e) {
      toast.error('Failed to delete wallet');
    }
  };

  const copyAddress = (address) => {
    navigator.clipboard.writeText(address);
    toast.success('Address copied');
  };

  const getNetworkLabel = (network) => {
    return NETWORK_OPTIONS.find(n => n.value === network)?.label || network;
  };

  const getNetworkColor = (network) => {
    if (network.includes('USDT')) return 'text-green-400 bg-green-500/10';
    if (network === 'BTC') return 'text-amber-400 bg-amber-500/10';
    if (network === 'ETH') return 'text-purple-400 bg-purple-500/10';
    if (network.includes('BNB')) return 'text-yellow-400 bg-yellow-500/10';
    return 'text-blue-400 bg-blue-500/10';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-100">Crypto Wallets</h1>
          <p className="text-sm text-gray-500 mt-1">Manage wallet addresses for receiving payments</p>
        </div>
        <button
          onClick={() => setCreating(true)}
          className="flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all text-sm font-medium"
        >
          <Plus size={16} />
          Add Wallet
        </button>
      </div>

      {/* Create form */}
      {creating && (
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 mb-6">
          <h3 className="text-sm font-semibold text-gray-200 mb-4">Add New Wallet</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={newWallet.network}
              onChange={(e) => setNewWallet({ ...newWallet, network: e.target.value })}
              className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200"
            >
              {NETWORK_OPTIONS.map(n => (
                <option key={n.value} value={n.value}>{n.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Wallet address"
              value={newWallet.address}
              onChange={(e) => setNewWallet({ ...newWallet, address: e.target.value })}
              className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200"
            />
            <input
              type="text"
              placeholder="Label (optional)"
              value={newWallet.label}
              onChange={(e) => setNewWallet({ ...newWallet, label: e.target.value })}
              className="bg-dark-surface border border-dark-border rounded-lg px-3 py-2 text-sm text-gray-200"
            />
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={createWallet} className="bg-emerald-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-emerald-600">Add</button>
            <button onClick={() => setCreating(false)} className="bg-gray-700 text-white px-4 py-2 rounded-lg text-sm hover:bg-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {/* Wallets grid */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : !wallets.length ? (
        <div className="text-center py-12 text-gray-500">
          <Wallet size={40} className="mx-auto mb-3 opacity-30" />
          <p>No wallets configured yet</p>
          <p className="text-xs mt-1">Add a wallet address to start receiving payments</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {wallets.map((w) => (
            <div key={w.id} className={`border rounded-xl p-5 transition-all ${w.isActive ? 'border-dark-border bg-dark-card' : 'border-dark-border/50 bg-dark-card/50 opacity-60'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${getNetworkColor(w.network)}`}>
                    {getNetworkLabel(w.network)}
                  </span>
                  {w.label && <span className="text-xs text-gray-500">{w.label}</span>}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleWallet(w.id, w.isActive)}
                    className={`p-1.5 rounded hover:bg-white/5 ${w.isActive ? 'text-emerald-400' : 'text-gray-600'}`}
                    title={w.isActive ? 'Disable' : 'Enable'}
                  >
                    {w.isActive ? <ToggleRight size={18} /> : <ToggleLeft size={18} />}
                  </button>
                  <button
                    onClick={() => setPendingDelete(w)}
                    className="p-1.5 rounded text-red-400/60 hover:text-red-400 hover:bg-red-400/10"
                    title="Delete"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-dark-surface rounded-lg px-3 py-2">
                <code className="text-xs text-gray-300 flex-1 truncate font-mono">{w.address}</code>
                <button
                  onClick={() => copyAddress(w.address)}
                  className="text-gray-500 hover:text-gray-300 flex-shrink-0"
                  title="Copy"
                >
                  <Copy size={14} />
                </button>
              </div>
              <p className="text-xs text-gray-600 mt-2">Added {w.createdAt?.split('T')[0] || 'N/A'}</p>
            </div>
          ))}
        </div>
      )}
      <AdminConfirmDialog
        open={Boolean(pendingDelete)}
        title="Delete wallet?"
        message={pendingDelete ? `${pendingDelete.network} wallet ${pendingDelete.label || pendingDelete.address} will be permanently removed.` : ''}
        confirmLabel="Delete Wallet"
        requireText={pendingDelete?.network || ''}
        onConfirm={deleteWallet}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
